import { View, Text, styled, useTheme } from 'tamagui'
import { useState, useRef, type ReactNode } from 'react'

const TooltipContent = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingHorizontal: 10,
  paddingVertical: 6,
  position: 'absolute',
  zIndex: 1000,
  // @ts-expect-error -- web pointer-events
  style: {
    pointerEvents: 'none',
  },
})

const TooltipText = styled(Text, {
  fontSize: 12,
  fontFamily: '$fontBody',
  color: '$surfaceForeground',
})

interface TooltipProps {
  content: string | ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  delay?: number
}

export function Tooltip({ content, children, side = 'right', delay = 300 }: TooltipProps) {
  const theme = useTheme()
  const [visible, setVisible] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const show = () => {
    timeoutRef.current = setTimeout(() => setVisible(true), delay)
  }

  const hide = () => {
    clearTimeout(timeoutRef.current)
    setVisible(false)
  }

  const positionStyle = {
    top: { bottom: '100%', left: '50%', marginBottom: 6, transform: 'translateX(-50%)' },
    right: { left: '100%', top: '50%', marginLeft: 6, transform: 'translateY(-50%)' },
    bottom: { top: '100%', left: '50%', marginTop: 6, transform: 'translateX(-50%)' },
    left: { right: '100%', top: '50%', marginRight: 6, transform: 'translateY(-50%)' },
  }[side]

  return (
    <View
      position="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {visible && (
        <TooltipContent
          // @ts-expect-error -- web positioning + shadow
          style={{ ...positionStyle, boxShadow: theme.shadowCard?.val || '0 4px 12px rgba(0,0,0,0.4)' }}
        >
          {typeof content === 'string' ? <TooltipText>{content}</TooltipText> : content}
        </TooltipContent>
      )}
    </View>
  )
}
